import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import {
  Drawer,
  DrawerSection,
  DrawerDetailRow,
  DrawerFooter,
} from '@/components/ui/drawer'
import { getEventById } from '@/api/events.api'
import type { EventItem } from '@/types/api'
import { MediaPreviewModal } from './MediaPreviewModal'
import type { PreviewFile } from './MediaPreviewModal'
import { formatDateTime } from '@/utils/format'
import {
  Upload as UploadIcon,
  Film,
  Image as ImageIcon,
  Music,
} from 'lucide-react'

interface EventDetailDrawerProps {
  eventId: string | null
  onClose: () => void
  onUpdate?: (event: EventItem) => void
  onDownload?: (url: string, fileName?: string) => void
}

export function EventDetailDrawer({
  eventId,
  onClose,
  onUpdate,
  onDownload,
}: EventDetailDrawerProps) {
  const [previewFile, setPreviewFile] = useState<PreviewFile | null>(null)

  const { data: eventData, isLoading } = useQuery({
    queryKey: ['event-detail', eventId],
    queryFn: ({ signal }) =>
      eventId ? getEventById(eventId, signal) : Promise.resolve(null),
    enabled: !!eventId,
    refetchOnMount: 'always',
    staleTime: 0,
    gcTime: 0,
  })

  // Đọc từ data gốc hoặc responseData
  const event = (eventData as any)?.responseData || eventData

  const mediaType = event?.mediaType || 1
  const eventMedias = event?.eventMedias || []
  const hasMediaFiles = eventMedias.length > 0

  // Helper render ô tệp media
  const renderMediaCell = (
    fileObj?: { fileUrl?: string; originFileName?: string } | null,
    type?: number,
  ) => {
    if (!fileObj || !fileObj.fileUrl) {
      return <span className="text-gray-400 italic text-sm">-</span>
    }

    return (
      <button
        type="button"
        className="flex items-center gap-2 cursor-pointer py-1 group w-fit max-w-full"
        onClick={() =>
          setPreviewFile({
            url: fileObj.fileUrl!,
            name: fileObj.originFileName || 'media_file',
            type,
          })
        }
      >
        <UploadIcon size={16} className="text-[#0E6DD2] shrink-0" />
        <span
          className="text-[#0E6DD2] italic font-normal truncate group-hover:underline text-sm max-w-[200px]"
          title={fileObj.originFileName}
        >
          {fileObj.originFileName || 'Tệp đính kèm'}
        </span>
      </button>
    )
  }

  return (
    <>
      <Drawer
        open={!!eventId}
        onClose={onClose}
        title="CHI TIẾT SỰ KIỆN"
        styles={{
          body: {
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
        footer={
          <DrawerFooter>
            <Button
              variant="outline"
              size="md"
              onClick={onClose}
              className="min-w-[140px] h-10"
            >
              Đóng
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={() => event && onUpdate?.(event)}
              className="min-w-[140px] h-10"
              disabled={!event || isLoading}
            >
              Cập nhật
            </Button>
          </DrawerFooter>
        }
      >
        {isLoading && (
          <div className="p-8 text-center text-gray-500 font-medium">
            Đang tải dữ liệu chi tiết...
          </div>
        )}

        {event && (
          <div className="flex flex-col flex-1 h-full overflow-y-auto p-1">
            {/* THÔNG TIN CHUNG */}
            <DrawerSection title="Thông tin chung">
              <DrawerDetailRow label="Tên sự kiện:" value={event.name} />
              <DrawerDetailRow
                label="Ngày bắt đầu:"
                value={formatDateTime(event.startDate, 'DD/MM/YYYY')}
              />
              <DrawerDetailRow
                label="Ngày kết thúc:"
                value={formatDateTime(event.endDate, 'DD/MM/YYYY')}
              />
              <DrawerDetailRow
                label="Trạng thái:"
                value={
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                      event.status === 1
                        ? 'bg-[#EAF7ED] text-[#2FB14F]'
                        : 'bg-[#FDECEB] text-[#EE4036]'
                    }`}
                  >
                    {event.status === 1 ? 'Hoạt động' : 'Không hoạt động'}
                  </span>
                }
              />
            </DrawerSection>

            {/* THÔNG TIN CHI TIẾT */}
            <DrawerSection title="Thông tin chi tiết">
              <DrawerDetailRow
                label="Loại nội dung:"
                value={
                  <span className="font-semibold text-[#1E2F41]">
                    {mediaType === 1 ? 'Video' : 'Tuyên truyền'}
                  </span>
                }
              />
              <DrawerDetailRow
                label="Thời gian hiển thị:"
                value={
                  mediaType === 1
                    ? 'Mặc định theo thời gian phát của video'
                    : `${event.mediaShowtime || 0} Giây`
                }
              />
            </DrawerSection>

            {/* NỘI DUNG / MẢNG MEDIA */}
            <DrawerSection title="Nội dung">
              {hasMediaFiles ? (
                <div className="border border-[rgba(30,47,65,0.25)] rounded-2xl overflow-hidden">
                  {/* Header Bảng */}
                  <div className="bg-[#F8F9FA] border-b border-[rgba(30,47,65,0.15)] py-3 px-4 text-xs font-bold text-[#1e2f41] uppercase flex items-center">
                    <div className="w-12 text-left pl-1">STT</div>
                    {mediaType === 1 ? (
                      <div className="flex-1 flex items-center gap-1.5">
                        <Film size={14} className="text-gray-500" />
                        VIDEO
                      </div>
                    ) : (
                      <>
                        <div className="flex-1 flex items-center gap-1.5">
                          <ImageIcon size={14} className="text-gray-500" />
                          HÌNH ẢNH
                        </div>
                        <div className="flex-1 flex items-center gap-1.5 border-l border-gray-200 pl-4">
                          <Music size={14} className="text-gray-500" />
                          ÂM THANH
                        </div>
                      </>
                    )}
                  </div>

                  {/* Body Bảng */}
                  <div className="divide-y divide-gray-100 bg-white">
                    {eventMedias.map((item: any, index: number) => (
                      <div
                        key={item.id || index}
                        className="px-4 flex items-center min-h-[52px]"
                      >
                        <div className="w-12 text-left pl-1 text-[#344054] font-medium text-sm">
                          {index + 1}
                        </div>

                        {/* Loại 1: Video */}
                        {mediaType === 1 && (
                          <div className="flex-1 pr-2">
                            {renderMediaCell(item.videoFileResponse, 1)}
                          </div>
                        )}

                        {/* Loại 2: Tuyên truyền (Hình ảnh & Âm thanh) */}
                        {mediaType === 2 && (
                          <>
                            <div className="flex-1 pr-4 border-r border-gray-100">
                              {renderMediaCell(item.imageFileResponse, 2)}
                            </div>
                            <div className="flex-1 pl-4">
                              {renderMediaCell(item.audioFileResponse, 3)}
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="py-8 text-center text-sm text-gray-400 bg-gray-50 rounded-2xl border border-dashed border-gray-200 mt-1">
                  Chưa có tệp tin nội dung nào.
                </div>
              )}
            </DrawerSection>
          </div>
        )}
      </Drawer>

      <MediaPreviewModal
        file={previewFile}
        onClose={() => setPreviewFile(null)}
        onDownload={onDownload}
      />
    </>
  )
}
